import { GridValidRowModel } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { editRecipientGroup } from "src/api/contractSweepRecipientSetting";
import DialogInOne, { DialogInOneProps } from "src/components/DialogInOne";
import { useAssets } from "src/components/FetchConfig";
import Prompter from "src/components/Prompter";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useAlerting, useTranslation, useYubiPrompt } from "src/hooks";
import { amountDivideDecimals, toDBInDecimals } from "src/utils/helper";
import { useZusDialogStore } from "src/zustand/store";
import EditGroupDialogContent from "../components/EditGroupDialogContent";
import { EditGroupFields } from "../types/EditGroupFields";
import { GetListRes } from "../types/GetListRes";
import { validateRotateAmountMaxAmountLimit } from "../helpers/validateRotateAmountMaxAmountLimit";
import { appError } from "src/utils/errorHandler";

type Props = {
  isOpen: boolean;
  selectedRow: (GridValidRowModel & { rawData: GetListRes }) | undefined;
  onClose: () => void;
};

const useEditGroupDialog = (props: Props) => {
  const { isOpen, onClose, selectedRow } = props;
  const { rawData } = selectedRow || {};

  //hooks
  const { translatePrefix, onFilterSearch } = usePageConfigProviderContext();
  const { getResAfterYubi, prompterConfig } = useYubiPrompt();
  const zusDialog = useZusDialogStore();
  const { t } = useTranslation(translatePrefix);
  const { selectAssetsByChain } = useAssets();
  const { alerting } = useAlerting();

  //state
  const [editGroupFields, setEditGroupFields] = useState<EditGroupFields>();

  const assets = selectAssetsByChain({
    chain_type: rawData?.chain_type,
    chain_id: rawData?.chain_id,
  });
  const selectedAsset = assets.find(
    (item) => item.asset_name === rawData?.asset_name,
  );

  useEffect(() => {
    setEditGroupFields({
      groupSequence: amountDivideDecimals(
        rawData?.group_seq?.toString() ?? "",
        0,
      ), // To avoid input label shirking issue caused by initial value is empty string or undefined
      status: rawData?.status?.toString(),
      maximumAmountLimit: amountDivideDecimals(
        rawData?.max_amount_limit ?? "",
        selectedAsset?.decimals,
      ),
      rotateAmountLimit: amountDivideDecimals(
        rawData?.rotate_group_amount_limit ?? "",
        selectedAsset?.decimals,
      ),
      unlimitAmount:
        Number(rawData?.max_amount_limit ?? 0) === 0 &&
        Number(rawData?.rotate_group_amount_limit ?? 0) === 0,
    });
  }, [rawData, selectedAsset?.decimals]);

  const validateForm = () => {
    return validateRotateAmountMaxAmountLimit(
      editGroupFields?.rotateAmountLimit,
      editGroupFields?.maximumAmountLimit,
      editGroupFields?.unlimitAmount,
    );
  };

  const onEditDialogConfirm = async () => {
    // validate
    const validationResult = validateForm();

    if (!validationResult.isValid) {
      alerting("error", t(validationResult.errorMsg));
      return;
    }

    const maxAmountLimit = editGroupFields?.unlimitAmount
      ? 0
      : toDBInDecimals(
          editGroupFields?.maximumAmountLimit ?? "",
          selectedAsset?.decimals ?? 0,
        );

    const rotateAmountLimit = editGroupFields?.unlimitAmount
      ? 0
      : toDBInDecimals(
          editGroupFields?.rotateAmountLimit ?? "",
          selectedAsset?.decimals ?? 0,
        );

    zusDialog.openExtra("loadingDialog");
    const res: any = await getResAfterYubi(
      editRecipientGroup,
      {
        groupId: rawData?.id,
        groupSeq: editGroupFields?.groupSequence,
        groupType: rawData?.group_type,
        maxAmountLimit,
        rotateAmountLimit,
        chainType: rawData?.chain_type,
        chainId: rawData?.chain_id,
        assetName: rawData?.asset_name,
        decimals: selectedAsset?.decimals,
        status: editGroupFields?.status,
      },
      {
        isSkipErrorHandling: true,
      },
    );
    zusDialog.closeExtra();

    if (!res) return;

    if ((res as any).error?.code === -51) {
      return alerting("warning", t("duplicate_group_sequence"));
    }

    if ((res as any).error?.code === -69) {
      return alerting("warning", t("active_group_cannot_be_deleted_disabled"));
    }

    if (!res || (res as any).error) {
      return appError({
        ...(res as any).error,
        requestId: (res as any).requestId,
      });
    }

    alerting("success", t("edit_record_success"));
    onFilterSearch();
    onClose();
  };

  const currentMaxAmountLimit = editGroupFields?.unlimitAmount
    ? 0
    : toDBInDecimals(
        editGroupFields?.maximumAmountLimit ?? "",
        selectedAsset?.decimals ?? 0,
      );
  const currentRotateAmountLimit = editGroupFields?.unlimitAmount
    ? 0
    : toDBInDecimals(
        editGroupFields?.rotateAmountLimit ?? "",
        selectedAsset?.decimals ?? 0,
      );

  const isConfirmDisabled =
    rawData?.group_seq?.toString() === editGroupFields?.groupSequence &&
    rawData?.status?.toString() === editGroupFields?.status &&
    rawData?.max_amount_limit?.toString() ===
      currentMaxAmountLimit?.toString() &&
    rawData?.rotate_group_amount_limit?.toString() ===
      currentRotateAmountLimit?.toString();

  const dialog: DialogInOneProps = {
    title: t("edit_record"),
    self: {
      open: isOpen,
      onClose: () => {
        setEditGroupFields(undefined);
        onClose();
      },
    },
    content: (
      <EditGroupDialogContent
        selectedRow={selectedRow}
        editGroupFields={editGroupFields}
        setEditGroupFields={setEditGroupFields}
      />
    ),
    isConfirmDisabled: isConfirmDisabled,
    onConfirm: onEditDialogConfirm,
    onCancel: onClose,
  };

  const renderEditGroupDialog = () => {
    return (
      <>
        <Prompter {...prompterConfig} />
        <DialogInOne {...dialog} />
      </>
    );
  };

  return { renderEditGroupDialog };
};

export default useEditGroupDialog;
